import { BRAND } from "./brand.js"

/**
 * ⚠ Brand amber (`#fbc624`) and sky (`#76cadd`) are deliberately **not** here. Feeding them in fails two checks
 * outright (lightness band 0.85 and 0.79 against a 0.77 ceiling; sky's chroma 0.086 against a 0.1 floor — it
 * reads as grey to a protanope). They stay where the brand guide actually puts them: the CTA and the stripe.
 */
export const SERIES_LIGHT = [
  BRAND.navy,
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
] as const

export const SERIES_DARK = [
  "#3e84c7",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
] as const

export const SERIES_SLOTS = 8

export const SEQUENTIAL_BLUE = [
  "#cce1f6",
  "#b0d2f6",
  "#94c4f5",
  "#79b5f1",
  "#60a5ea",
  "#4896e1",
  "#3287d4",
  "#2078c4",
  "#126ab1",
  "#0c5c9b",
  "#0f4e84",
  "#13416b",
  "#163552",
] as const

/**
 * ⚠ These are mark colours and several of them are unreadable as text: brand amber is 1.59:1 against white.
 * Text and small icons use `STATUS_INK` instead — same rule as the series palette, where text wears ink tokens
 * and only the mark wears the colour.
 */
export const STATUS = {
  good: BRAND.green,
  warning: BRAND.amber,
  serious: BRAND.orange,
  critical: BRAND.red,
} as const

export const STATUS_INK = {
  light: { good: "#1a8040", warning: "#92400e", serious: "#9a3412", critical: "#c0392b" },
  dark: { good: "#4ec47c", warning: "#f0b429", serious: "#f5934d", critical: "#f07171" },
} as const

export const STATUS_CHIP = {
  light: {
    good: { surface: "#f0faf4", border: "#aee0be", text: "#1a8040" },
    warning: { surface: "#fffbf0", border: "#fde68a", text: "#92400e" },
    serious: { surface: "#fff7ed", border: "#fed7aa", text: "#9a3412" },
    critical: { surface: "#fff5f5", border: "#ffc9c9", text: "#c0392b" },
    info: { surface: BRAND.ice, border: BRAND.lightBlue, text: BRAND.navy },
  },
  dark: {
    good: { surface: "#12281b", border: "#2c5638", text: "#4ec47c" },
    warning: { surface: "#2b2110", border: "#5a4520", text: "#f0b429" },
    serious: { surface: "#2c1c11", border: "#5b3a22", text: "#f5934d" },
    critical: { surface: "#2c1616", border: "#5c2e2e", text: "#f07171" },
    info: { surface: "#12232e", border: "#27455a", text: "#5aa3e0" },
  },
} as const

export const SURFACE = { light: BRAND.white, dark: BRAND.text } as const

export const SURFACE_SUBTLE = { light: BRAND.ice, dark: "#242322" } as const

export const INK = {
  light: {
    primary: BRAND.text,
    secondary: BRAND.textSecondary,
    muted: BRAND.textMuted,
    gridline: BRAND.border,
    baseline: BRAND.lightBlue,
    deltaGood: "#1a8040",
  },
  dark: {
    primary: "#f5f5f3",
    secondary: "#b8b6b0",
    muted: "#8a8882",
    gridline: "#33322f",
    baseline: "#4a4945",
    deltaGood: "#4ec47c",
  },
} as const

export type ThemeMode = "light" | "dark"

export const seriesPalette = (mode: ThemeMode): readonly string[] =>
  mode === "dark" ? SERIES_DARK : SERIES_LIGHT

export const ink = (mode: ThemeMode) => INK[mode]
export const surface = (mode: ThemeMode) => SURFACE[mode]
export const surfaceSubtle = (mode: ThemeMode) => SURFACE_SUBTLE[mode]
export const statusInk = (mode: ThemeMode) => STATUS_INK[mode]
export const statusChip = (mode: ThemeMode) => STATUS_CHIP[mode]

export function tenantColor(colorSlot: number, mode: ThemeMode): string {
  const palette = seriesPalette(mode)
  return palette[(colorSlot - 1) % SERIES_SLOTS]!
}

export function ordinalColor(index: number, mode: ThemeMode): string {
  return seriesPalette(mode)[index % SERIES_SLOTS]!
}

export function sequentialColor(t: number): string {
  const clamped = Math.min(1, Math.max(0, t))
  const i = Math.round(clamped * (SEQUENTIAL_BLUE.length - 1))
  return SEQUENTIAL_BLUE[i]!
}

/**
 * ⚠ One measure returns **series slot 1**, the same blue as every other single-series chart.
 */
export function singleHueColor(index: number, count: number, mode: ThemeMode): string {
  if (count <= 1) return seriesPalette(mode)[0]!
  const [lo, hi] = mode === "dark" ? [0, 0.42] : [0.45, 1]
  return sequentialColor(lo! + (index / (count - 1)) * (hi! - lo!))
}

export const LOW_CONTRAST_SLOTS_LIGHT = [3, 4, 5] as const

export function needsLabelRelief(colorSlot: number, mode: ThemeMode): boolean {
  return mode === "light" && (LOW_CONTRAST_SLOTS_LIGHT as readonly number[]).includes(colorSlot)
}
